/**
 * ProjectPicker — дропдаун в Header для выбора активного проекта.
 *
 * Подписан на useProjectStore.currentProjectId (реактивно).
 * Подписан на useProjects() — список проектов текущего account'а.
 *
 * Резолв currentProjectId (stale id → «Default» → lazy-create) выполняет
 * useProjectBootstrap, смонтированный в Shell. Picker — единственная точка
 * смены currentProjectId из Header; при смене/создании синхронно
 * обновляет и store, и URL ?project= (см. useSyncUrlProject ниже),
 * чтобы Pages (FilesUpload, FilesResults) не рассинхронизировались.
 *
 * UX:
 *  - Кнопка с иконкой Folder + имя текущего проекта
 *  - При клике — выпадающий список со всеми проектами (archived — помечены)
 *  - Создание нового проекта — внутри дропдауна (useCreateProject + setCurrent
 *    + syncUrl; inline ошибка при сбое API)
 *  - «Открыть /settings/projects» — для полного управления
 */
import { ChevronDown, Folder, Plus, Settings } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useProjectStore } from '@/store/projectStore';
import { useCreateProject, useProjects } from '@/hooks/useProjects';

/**
 * Синхронизирует URL ?project= с store currentProjectId.
 * Вызывается после каждой успешной смены проекта (Picker и Create).
 * Без этого Pages читают projectIdFromUrl и рассинхронизируются
 * (см. регрессию 2026-07-26 в PROGRESS.md).
 */
function useSyncUrlProject(): (id: string) => void {
  const [searchParams, setSearchParams] = useSearchParams();
  return (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('project', id);
    setSearchParams(next, { replace: true });
  };
}

export function ProjectPicker() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentId = useProjectStore((s) => s.currentProjectId);
  const setCurrent = useProjectStore((s) => s.setCurrent);
  const syncUrl = useSyncUrlProject();
  const { data, isLoading } = useProjects();
  const createProject = useCreateProject();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  const projects = data?.items ?? [];
  const current = projects.find((p) => p.id === currentId) ?? null;

  const handleSelect = (id: string) => {
    setCurrent(id);
    syncUrl(id);
    setOpen(false);
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreateError(null);
    try {
      const created = await createProject.mutateAsync(name);
      setCurrent(created.id);
      syncUrl(created.id);
      setNewName('');
      setCreating(false);
      setOpen(false);
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? t('common.error');
      setCreateError(detail);
      // eslint-disable-next-line no-console
      console.error('[ProjectPicker] create project failed', err);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-surface-900 border border-surface-800 hover:border-surface-700 text-sm min-w-[180px] max-w-[280px]"
        title={current?.name ?? t('header.projectPicker.chooseProject', 'Выбрать проект')}
      >
        <Folder className="w-4 h-4 text-primary-400 flex-shrink-0" />
        <span className="truncate flex-1 text-left">
          {isLoading
            ? t('header.projectPicker.loading', 'Загрузка…')
            : current?.name ?? t('header.projectPicker.noProject', 'Без проекта')}
        </span>
        <ChevronDown className={`w-4 h-4 text-surface-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          {/* Click-outside catcher */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <div
            className="absolute left-0 top-full mt-2 w-72 rounded-xl bg-surface-900 border border-surface-800 shadow-xl z-50 overflow-hidden"
            onMouseLeave={() => !creating && setOpen(false)}
          >
            <div className="px-3 py-2 border-b border-surface-800 flex items-center justify-between">
              <span className="text-xs uppercase tracking-wide text-surface-500">
                {t('header.projectPicker.projects', 'Проекты')}
              </span>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate('/settings/projects');
                }}
                className="text-xs text-primary-400 hover:text-primary-300 inline-flex items-center gap-1"
              >
                <Settings className="w-3 h-3" />
                {t('header.projectPicker.manage', 'Управление')}
              </button>
            </div>

            <div className="max-h-64 overflow-y-auto">
              {projects.length === 0 && !isLoading && (
                <div className="px-3 py-4 text-sm text-surface-500 text-center">
                  {t('header.projectPicker.empty', 'Нет проектов')}
                </div>
              )}
              {projects.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => handleSelect(p.id)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-surface-800/60 flex items-center gap-2 ${
                    p.id === currentId ? 'bg-surface-800/40 text-primary-300' : ''
                  }`}
                >
                  <Folder className="w-3.5 h-3.5 text-surface-500 flex-shrink-0" />
                  <span className="truncate flex-1">{p.name}</span>
                  {p.is_archived && (
                    <span className="text-[10px] uppercase text-surface-500 tracking-wide">
                      {t('header.projectPicker.archived', 'архив')}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="border-t border-surface-800 p-2">
              {creating ? (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <input
                      autoFocus
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleCreate();
                        if (e.key === 'Escape') {
                          setCreating(false);
                          setNewName('');
                          setCreateError(null);
                        }
                      }}
                      placeholder={t('header.projectPicker.newPlaceholder', 'Название проекта')}
                      className="flex-1 px-2 py-1 text-sm rounded-lg bg-surface-950 border border-surface-800 focus:border-primary-500 outline-none"
                    />
                    <button
                      type="button"
                      onClick={handleCreate}
                      disabled={!newName.trim() || createProject.isPending}
                      className="px-2 py-1 text-xs rounded-lg bg-primary-500 hover:bg-primary-600 text-white disabled:opacity-50"
                    >
                      {t('common.create', 'Создать')}
                    </button>
                  </div>
                  {createError && (
                    <div className="text-[11px] text-danger px-1">
                      {createError}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setCreating(true);
                    setCreateError(null);
                  }}
                  className="w-full text-left px-2 py-1.5 text-sm text-surface-400 hover:text-surface-200 hover:bg-surface-800/60 rounded-lg inline-flex items-center gap-2"
                >
                  <Plus className="w-3.5 h-3.5" />
                  {t('header.projectPicker.newProject', 'Новый проект')}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
